import { useEffect, useState, useCallback } from "react";
import { getCycleHistory, getCycleHistoryRecord } from "@/api";
import { toCustomerText } from "@/lib/personalizationPoints";

// Read-only Past Cycles surface. It consumes ONLY the read-only history endpoints and NEVER calls
// any mutation (no edit/delete/mark-done/generate/lifecycle). It is fully isolated: a history error
// shows a friendly message here and can never touch or corrupt the current cycle's Vault.
const STATUS_LABEL = { draft: "Draft", ready: "Ready", posted: "Posted" };
const statusLabel = (s) => STATUS_LABEL[s] || "Draft";

function fmtDate(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function recordTitle(rec) {
  const f = rec.activated_focus || rec.current_focus || {};
  return (f.searchableTitle || f.brand || "Findability Cycle").trim() || "Findability Cycle";
}

export default function PastCycles() {
  const [records, setRecords] = useState(null); // null = loading
  const [listError, setListError] = useState("");
  const [selected, setSelected] = useState(null); // opened record
  const [recordBusy, setRecordBusy] = useState(false);
  const [recordError, setRecordError] = useState("");
  const [copiedId, setCopiedId] = useState("");

  useEffect(() => {
    let cancelled = false;
    getCycleHistory()
      .then((res) => { if (!cancelled) setRecords(res.records || []); })
      .catch(() => { if (!cancelled) { setRecords([]); setListError("We couldn't load your past cycles just now. Please try again shortly."); } });
    return () => { cancelled = true; };
  }, []);

  const openRecord = useCallback(async (cycleId) => {
    setRecordBusy(true);
    setRecordError("");
    try {
      const res = await getCycleHistoryRecord(cycleId);
      setSelected(res.record);
    } catch {
      setRecordError("We couldn't open that cycle just now. Please try again shortly.");
    } finally {
      setRecordBusy(false);
    }
  }, []);

  const copyPost = useCallback(async (id, text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text || "");
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(""), 1500);
    } catch { /* copy is best-effort and never mutates data */ }
  }, []);

  // ── Opened single cycle: read-only saved posts ──
  if (selected) {
    const items = selected.vault || [];
    const outcome = selected.is_completed ? "Completed" : "Closed early";
    const when = fmtDate(selected.timestamps && (selected.timestamps.completed_at || selected.timestamps.closed_at));
    return (
      <div className="tab-pane" role="tabpanel" data-testid="past-cycle-record">
        <button className="btn-tiny" onClick={() => setSelected(null)} data-testid="past-cycle-back">← Back to Past Cycles</button>
        <h2 className="section-h2" style={{ marginTop: 12 }} data-testid="past-cycle-record-title">{recordTitle(selected)}</h2>
        <p className="section-sub" data-testid="past-cycle-record-meta">
          {outcome}{when ? ` · ${when}` : ""} · This cycle is complete and read-only. Your saved posts are preserved below.
        </p>
        <div data-testid="past-cycle-vault-list">
          {items.length === 0 ? (
            <div className="empty-state" data-testid="past-cycle-vault-empty">No saved posts were stored in this cycle.</div>
          ) : (
            items.map((item) => {
              const status = item.status || "draft";
              const text = toCustomerText(item.text);
              return (
                <div className="vault-item vault-item-full" key={item.id} data-testid={`past-cycle-vault-item-${item.id}`}>
                  <div className="vault-head">
                    <div>
                      <div className="vault-name">Effort {item.day} — {item.platformLabel || ""}</div>
                      <div className="vault-meta">{item.theme}{item.saved ? ` · ${item.saved}` : ""}</div>
                    </div>
                    <span className={`status-badge status-${status}`} data-testid={`past-cycle-vault-status-badge-${item.id}`}>{statusLabel(status)}</span>
                  </div>
                  <div
                    className="vault-edit-area"
                    data-testid={`past-cycle-vault-text-${item.id}`}
                    style={{ whiteSpace: "pre-wrap", minHeight: "auto", cursor: "default" }}
                    aria-readonly="true"
                  >{text}</div>
                  {item.imageRef ? (
                    <div className="vault-meta" style={{ marginTop: 6 }} data-testid={`past-cycle-vault-imageref-${item.id}`}>
                      Image reference: {item.imageRef}
                    </div>
                  ) : null}
                  <div className="vault-actions">
                    <button className="btn-tiny" onClick={() => copyPost(item.id, text)} data-testid={`past-cycle-vault-copy-${item.id}`}>
                      {copiedId === item.id ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ── Index: list terminal cycles ──
  return (
    <div className="tab-pane" role="tabpanel" data-testid="past-cycles">
      <h2 className="section-h2">Past Cycles</h2>
      <p className="section-sub">Completed cycles stay here, read-only, so you can revisit every saved post you created.</p>
      {records === null ? (
        <div className="empty-state" data-testid="past-cycles-loading">Loading your past cycles…</div>
      ) : records.length === 0 ? (
        <div className="empty-state" data-testid="past-cycles-empty">
          {listError || "You don't have any completed cycles yet. When you finish a Findability Cycle, it will appear here with all its saved posts."}
        </div>
      ) : (
        <div data-testid="past-cycles-list">
          {recordError ? <div className="empty-state" data-testid="past-cycle-record-error">{recordError}</div> : null}
          {records.map((rec) => {
            const when = fmtDate(rec.timestamps && (rec.timestamps.completed_at || rec.timestamps.closed_at));
            const outcome = rec.is_completed ? "Completed" : "Closed early";
            const savedCount = (rec.vault || []).length;
            return (
              <div className="vault-item vault-item-full" key={rec.cycle_id} data-testid={`past-cycle-item-${rec.cycle_id}`}>
                <div className="vault-head">
                  <div>
                    <div className="vault-name">{recordTitle(rec)}</div>
                    <div className="vault-meta">{outcome}{when ? ` · ${when}` : ""} · {savedCount} saved post{savedCount === 1 ? "" : "s"}</div>
                  </div>
                  <button
                    className="btn-tiny"
                    disabled={recordBusy}
                    onClick={() => openRecord(rec.cycle_id)}
                    data-testid={`past-cycle-open-${rec.cycle_id}`}
                  >
                    {recordBusy ? "Opening…" : "Open"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
